import React, { useState, useEffect, useRef } from 'react';
import {
  Card, Form, Input, Select, Button, Typography, Space,
  Spin, Popconfirm, Modal, DatePicker, TimePicker, message, Row, Col, List, Tooltip, Empty, Radio, InputNumber, Switch, theme as antdTheme,
} from 'antd';
import {
  MailOutlined, ArrowLeftOutlined, SendOutlined,
  ClockCircleOutlined, CheckCircleOutlined, SaveOutlined,
  FolderOpenOutlined, DeleteOutlined, UserAddOutlined, StopOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import dayjs from 'dayjs';
import EmailEditor from 'react-email-editor';

const { Title, Text } = Typography;
const { Option } = Select;

// ── Common IANA timezones for schedule picker ────────────────────────────
const TIMEZONES = [
  { label: 'UTC (UTC+0)',                     value: 'UTC' },
  { label: 'London (UTC+0/+1)',               value: 'Europe/London' },
  { label: 'Paris / Berlin (UTC+1/+2)',       value: 'Europe/Paris' },
  { label: 'Helsinki / Kyiv (UTC+2/+3)',      value: 'Europe/Helsinki' },
  { label: 'Moscow (UTC+3)',                  value: 'Europe/Moscow' },
  { label: 'Dubai (UTC+4)',                   value: 'Asia/Dubai' },
  { label: 'Karachi (UTC+5)',                 value: 'Asia/Karachi' },
  { label: 'India (UTC+5:30)',                value: 'Asia/Kolkata' },
  { label: 'Dhaka (UTC+6)',                   value: 'Asia/Dhaka' },
  { label: 'Bangkok / Jakarta (UTC+7)',       value: 'Asia/Bangkok' },
  { label: 'Singapore / Beijing (UTC+8)',     value: 'Asia/Singapore' },
  { label: 'Tokyo / Seoul (UTC+9)',           value: 'Asia/Tokyo' },
  { label: 'Sydney (UTC+10/+11)',             value: 'Australia/Sydney' },
  { label: 'Auckland (UTC+12/+13)',           value: 'Pacific/Auckland' },
  { label: 'Honolulu (UTC-10)',               value: 'Pacific/Honolulu' },
  { label: 'Anchorage (UTC-9/-8)',            value: 'America/Anchorage' },
  { label: 'Los Angeles / Seattle (UTC-8/-7)',value: 'America/Los_Angeles' },
  { label: 'Denver / Phoenix (UTC-7/-6)',     value: 'America/Denver' },
  { label: 'Chicago / Dallas (UTC-6/-5)',     value: 'America/Chicago' },
  { label: 'New York / Miami (UTC-5/-4)',     value: 'America/New_York' },
  { label: 'Toronto (UTC-5/-4)',              value: 'America/Toronto' },
  { label: 'São Paulo (UTC-3)',               value: 'America/Sao_Paulo' },
];

// Weekday values match the backend's convention: Date.getUTCDay() —
// 0=Sun, 1=Mon, 2=Tue, 3=Wed, 4=Thu, 5=Fri, 6=Sat. Displayed Monday-first.
const WEEKDAYS = [
  { label: 'M',   full: 'Monday',    value: 1 },
  { label: 'T',   full: 'Tuesday',   value: 2 },
  { label: 'W',   full: 'Wednesday', value: 3 },
  { label: 'T',   full: 'Thursday',  value: 4 },
  { label: 'F',   full: 'Friday',    value: 5 },
  { label: 'S',   full: 'Saturday',  value: 6 },
  { label: 'Sun', full: 'Sunday',    value: 0 },
];
const dayLabel = (v) => (WEEKDAYS.find(d => d.value === v)?.full.slice(0, 3)) || '?';

// ── Unlayer merge-tag config ─────────────────────────────────────────────
const MERGE_TAGS = {
  first_name: { name: 'First Name',  value: '{{first_name}}', sample: 'John'              },
  full_name:  { name: 'Full Name',   value: '{{full_name}}',  sample: 'John Smith'         },
  email:      { name: 'Email',       value: '{{email}}',      sample: 'john@example.com'   },
  city:       { name: 'City',        value: '{{city}}',       sample: 'New York'           },
  state:      { name: 'State',       value: '{{state}}',      sample: 'NY'                 },
  country:    { name: 'Country',     value: '{{country}}',    sample: 'US'                 },
};

// ─────────────────────────────────────────────────────────────────────────
const CreateCampaign = () => {
  const { token: themeToken } = antdTheme.useToken();
  const { id: campaignId } = useParams();
  const isEdit = !!campaignId;

  const [lists,          setLists]          = useState([]);
  const [saving,         setSaving]         = useState(false);
  const [loading,        setLoading]        = useState(isEdit);
  const [testModal,      setTestModal]      = useState(false);
  const [testEmail,      setTestEmail]      = useState('');
  const [testSending,    setTestSending]    = useState(false);
  const [campaignStatus, setCampaignStatus] = useState('draft');
  const [sending,        setSending]        = useState(false);
  const [scheduleModal,  setScheduleModal]  = useState(false);
  const [scheduleDate,   setScheduleDate]   = useState(null);
  const [scheduleTz,     setScheduleTz]     = useState(() => Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [scheduling,     setScheduling]     = useState(false);
  const [sendMode,       setSendMode]       = useState('all');   // 'all' | 'daily'
  const [sendDaily,      setSendDaily]      = useState(200);
  const [sendDayMode,       setSendDayMode]       = useState('every'); // 'every' | 'specific'
  const [sendDaysSelected,  setSendDaysSelected]  = useState([1, 2, 3, 4, 5]);
  const [sendFixedTime,  setSendFixedTime]  = useState(false);
  const [sendDailyClock, setSendDailyClock] = useState(null); // dayjs — used in "every day" mode
  const [sendDayTimes,   setSendDayTimes]   = useState({});   // { [dayValue]: dayjs } — used in "specific days" mode, one time per day
  const [sendDailyTz,    setSendDailyTz]    = useState(() => Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [sendTiming,     setSendTiming]     = useState('now');   // 'now' | 'schedule'
  const [editorReady,    setEditorReady]    = useState(false);
  const [designToLoad,   setDesignToLoad]   = useState(null);
  const [uniqueCount,    setUniqueCount]    = useState(null);

  // Template state
  const [saveTemplModal,  setSaveTemplModal]  = useState(false);
  const [loadTemplModal,  setLoadTemplModal]  = useState(false);
  const [templName,       setTemplName]       = useState('');
  const [templSaving,     setTemplSaving]     = useState(false);
  const [templates,       setTemplates]       = useState([]);
  const [templLoading,    setTemplLoading]    = useState(false);

  const emailEditorRef = useRef(null);
  const [form]         = Form.useForm();
  const navigate       = useNavigate();
  const location       = useLocation();

  const subjectValue     = Form.useWatch('subject',      form) || '';
  const previewTextValue = Form.useWatch('preview_text', form) || '';
  const subjectLen       = subjectValue.length;
  const subjectColor     = subjectLen <= 50 ? '#9DC533' : subjectLen <= 80 ? '#f59e0b' : '#ef4444';

  // ── Lists ───────────────────────────────────────────────────────────
  useEffect(() => {
    axios.get('/api/lists')
      .then(res => setLists(res.data))
      .catch(() => message.error('Failed to load lists'));
  }, []);

  // ── Load campaign for edit ──────────────────────────────────────────
  useEffect(() => {
    if (!isEdit) return;
    axios.get(`/api/campaigns/${campaignId}`)
      .then(res => {
        form.setFieldsValue({
          name:             res.data.name,
          subject:          res.data.subject,
          list_id:          Array.isArray(res.data.list_id) ? res.data.list_id : (res.data.list_id ? [res.data.list_id] : []),
          preview_text:     res.data.preview_text     || '',
          reply_to:         res.data.reply_to         || '',
          exclude_list_ids: res.data.exclude_list_ids || [],
          exclude_emails:   res.data.exclude_emails   || [],
          manual_emails:    res.data.manual_emails     || [],
        });
        setCampaignStatus(res.data.status || 'draft');
        const loadedListIds = Array.isArray(res.data.list_id) ? res.data.list_id : (res.data.list_id ? [res.data.list_id] : []);
        fetchReachCount(loadedListIds, res.data.exclude_list_ids, res.data.exclude_emails);
        if (res.data.design_json) {
          try { setDesignToLoad(JSON.parse(res.data.design_json)); } catch {}
        }
      })
      .catch(() => message.error('Failed to load campaign'))
      .finally(() => setLoading(false));
  }, [campaignId]);

  // ── Pre-load template design passed via navigation state ────────────
  useEffect(() => {
    if (!isEdit && location.state?.templateDesign) {
      try {
        const design = typeof location.state.templateDesign === 'string'
          ? JSON.parse(location.state.templateDesign)
          : location.state.templateDesign;
        setDesignToLoad(design);
      } catch {}
    }
  }, []);

  // ── Load saved design once editor is ready ──────────────────────────
  useEffect(() => {
    if (editorReady && designToLoad && emailEditorRef.current) {
      emailEditorRef.current.editor.loadDesign(designToLoad);
    }
  }, [editorReady, designToLoad]);

  const onEditorReady = () => setEditorReady(true);

  const getEditorContent = () =>
    new Promise((resolve) => {
      if (!emailEditorRef.current?.editor) { resolve({ html: '', design: null }); return; }
      emailEditorRef.current.editor.exportHtml(({ design, html }) => resolve({ html, design }));
    });

  // ── Save helper ─────────────────────────────────────────────────────
  const saveDraft = async () => {
    const vals = await form.validateFields();
    const { html, design } = await getEditorContent();
    if (!html || html.replace(/<[^>]+>/g, '').trim() === '') {
      throw new Error('Email content is empty. Add some content in the builder first.');
    }
    const payload = {
      ...vals,
      html_content:     html,
      design_json:      design ? JSON.stringify(design) : null,
      exclude_list_ids: vals.exclude_list_ids || [],
      exclude_emails:   vals.exclude_emails   || [],
      manual_emails:    vals.manual_emails    || [],
    };
    if (campaignId) {
      await axios.put(`/api/campaigns/${campaignId}`, payload);
      return campaignId;
    }
    const res = await axios.post('/api/campaigns', payload);
    return res.data.id;
  };

  // Fetches the reach count with the SAME exclusion rules the actual send
  // applies (suppression + Exclude Lists + Exclude Emails) — not just the
  // raw Mailing Lists selection — so the number shown always matches who
  // will really be mailed.
  const fetchReachCount = async (listIds, excludeListIds, excludeEmails) => {
    if (!listIds || !listIds.length) { setUniqueCount(null); return; }
    try {
      const params = new URLSearchParams({ list_ids: listIds.join(',') });
      if (excludeListIds?.length) params.set('exclude_list_ids', excludeListIds.join(','));
      if (excludeEmails?.length) params.set('exclude_emails', excludeEmails.join(','));
      const res = await axios.get(`/api/lists/reach?${params.toString()}`);
      setUniqueCount(res.data.unique_count);
    } catch { setUniqueCount(null); }
  };

  // Recompute reach from whatever is currently in the form — called whenever
  // Mailing Lists, Exclude Lists, or Exclude Emails changes, so excluding
  // someone is reflected immediately, not just the initial list pick.
  const recalcReach = () => {
    fetchReachCount(
      form.getFieldValue('list_id'),
      form.getFieldValue('exclude_list_ids'),
      form.getFieldValue('exclude_emails'),
    );
  };

  // ── Handlers ────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    setSaving(true);
    try {
      await saveDraft();
      navigate('/campaigns');
    } catch (err) {
      message.error(err.message || err.response?.data?.message || 'Failed to save campaign');
    } finally { setSaving(false); }
  };

  const handleTestSend = async () => {
    if (!testEmail) return;
    setTestSending(true);
    try {
      // Always persist the current editor content first — otherwise a test
      // sent after unsaved edits would use the stale, already-saved version.
      const wasNew = !campaignId;
      const id = await saveDraft();
      if (wasNew) navigate(`/campaigns/${id}/edit`, { replace: true });
      await axios.post(`/api/campaigns/${id}/test`, { email: testEmail });
      message.success(`Test email sent to ${testEmail}`);
      setTestModal(false); setTestEmail('');
    } catch (err) {
      message.error(err.response?.data?.message || err.message || 'Test send failed');
    } finally { setTestSending(false); }
  };

  // Convert the picked wall-clock time (in scheduleTz) to a UTC ISO string.
  const toScheduleIso = () => {
    const d = dayjs(scheduleDate);
    const wallClock = new Date(Date.UTC(d.year(), d.month(), d.date(), d.hour(), d.minute(), 0));
    const tzParts = (zone) => new Intl.DateTimeFormat('en-US', {
      timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
    }).formatToParts(wallClock).reduce((a, p) => { if (p.type !== 'literal') a[p.type] = +p.value; return a; }, {});
    const toMs = (o) => Date.UTC(o.year, o.month - 1, o.day, o.hour % 24, o.minute, o.second);
    const offsetMs = toMs(tzParts(scheduleTz)) - toMs(tzParts('UTC'));
    return new Date(wallClock.getTime() - offsetMs).toISOString();
  };

  // Convert a chosen time-of-day (dayjs, hour/minute only) in `tz` to a UTC
  // "HH:mm" string, anchored to today's date in `tz` for the conversion.
  const toUTCTimeString = (dayjsTime, tz) => {
    const anchored = dayjs().hour(dayjsTime.hour()).minute(dayjsTime.minute()).second(0);
    const d = new Date(Date.UTC(anchored.year(), anchored.month(), anchored.date(), anchored.hour(), anchored.minute(), 0));
    const tzParts = (zone) => new Intl.DateTimeFormat('en-US', {
      timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
    }).formatToParts(d).reduce((a, p) => { if (p.type !== 'literal') a[p.type] = +p.value; return a; }, {});
    const toMs2 = (o) => Date.UTC(o.year, o.month - 1, o.day, o.hour % 24, o.minute, o.second);
    const offset = toMs2(tzParts(tz)) - toMs2(tzParts('UTC'));
    const utc = new Date(d.getTime() - offset);
    return `${String(utc.getUTCHours()).padStart(2, '0')}:${String(utc.getUTCMinutes()).padStart(2, '0')}`;
  };

  // Toggle a weekday in/out of sendDaysSelected, keeping sendDayTimes in
  // sync — a newly-checked day gets a sensible default time (copied from
  // the most recently added day, or 9:00 AM), an unchecked day drops its entry.
  const toggleSendDay = (dayValue) => {
    setSendDaysSelected(prev => {
      const isActive = prev.includes(dayValue);
      if (isActive) {
        setSendDayTimes(pt => { const c = { ...pt }; delete c[dayValue]; return c; });
        return prev.filter(v => v !== dayValue);
      }
      setSendDayTimes(pt => {
        if (pt[dayValue]) return pt;
        const lastDay = prev[prev.length - 1];
        return { ...pt, [dayValue]: pt[lastDay] || dayjs().hour(9).minute(0) };
      });
      return [...prev, dayValue];
    });
  };

  const handleUnifiedSend = async () => {
    const body = {};
    if (sendMode === 'daily' && sendDaily > 0) {
      body.dailyLimit = sendDaily;
      if (sendDayMode === 'specific') {
        if (!sendDaysSelected.length) { message.warning('Select at least one day'); return; }
        body.sendDays = sendDaysSelected;
      }
      if (sendFixedTime) {
        if (sendDayMode === 'specific') {
          const times = {};
          for (const d of sendDaysSelected) {
            const t = sendDayTimes[d];
            if (!t) { message.warning(`Pick a time for ${dayLabel(d)}`); return; }
            times[d] = toUTCTimeString(dayjs(t), sendDailyTz);
          }
          body.dayTimesUTC = times;
          body.dailyTimezone = sendDailyTz;
        } else {
          if (!sendDailyClock) { message.warning('Pick a daily send time'); return; }
          body.dailyTimeUTC = toUTCTimeString(dayjs(sendDailyClock), sendDailyTz);
          body.dailyTimezone = sendDailyTz;
        }
      }
    }
    if (sendTiming === 'schedule') {
      if (!scheduleDate) { message.warning('Please select a date and time'); return; }
      const iso = toScheduleIso();
      if (new Date(iso) <= new Date()) { message.error('Scheduled time must be in the future'); return; }
      body.scheduleAt = iso;
    } else {
      body.sendNow = true;
    }
    setScheduling(true);
    try {
      const id = await saveDraft();
      const res = await axios.post(`/api/campaigns/${id}/send`, body);
      message.success(res.data.message || 'Campaign queued!');
      setScheduleModal(false); setScheduleDate(null);
      navigate('/campaigns');
    } catch (err) {
      message.error(err.message || err.response?.data?.message || 'Failed to send campaign');
    } finally { setScheduling(false); }
  };

  // ── Template handlers ───────────────────────────────────────────────
  const handleSaveTemplate = async () => {
    if (!templName.trim()) { message.warning('Enter a template name'); return; }
    setTemplSaving(true);
    try {
      const { html, design } = await getEditorContent();
      if (!html || html.replace(/<[^>]+>/g, '').trim() === '') {
        message.warning('Builder is empty — add some content first'); return;
      }
      await axios.post('/api/templates', {
        name: templName.trim(),
        design_json: design ? JSON.stringify(design) : null,
        html,
      });
      message.success(`Template "${templName.trim()}" saved!`);
      setSaveTemplModal(false);
      setTemplName('');
    } catch {
      message.error('Failed to save template');
    } finally { setTemplSaving(false); }
  };

  const openLoadTemplates = async () => {
    setLoadTemplModal(true);
    setTemplLoading(true);
    try {
      const res = await axios.get('/api/templates');
      setTemplates(res.data);
    } catch {
      message.error('Failed to load templates');
    } finally { setTemplLoading(false); }
  };

  const handleLoadTemplate = (tpl) => {
    if (!tpl.design_json) { message.warning('This template has no design data'); return; }
    try {
      const design = JSON.parse(tpl.design_json);
      emailEditorRef.current?.editor?.loadDesign(design);
      message.success(`Loaded "${tpl.name}"`);
      setLoadTemplModal(false);
    } catch {
      message.error('Failed to load template design');
    }
  };

  const handleDeleteTemplate = async (id, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`/api/templates/${id}`);
      setTemplates(prev => prev.filter(t => t.id !== id));
      message.success('Template deleted');
    } catch {
      message.error('Failed to delete template');
    }
  };

  // ── Render ──────────────────────────────────────────────────────────
  if (loading) return <div style={{ textAlign: 'center', paddingTop: 80 }}><Spin size="large" /></div>;

  const isDraft = campaignStatus === 'draft';

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/campaigns')}
          style={{ padding: 0, color: themeToken.colorTextSecondary, marginBottom: 8 }}
        >
          Back to Campaigns
        </Button>
        <Title level={3} style={{ margin: 0 }}>
          {isEdit ? 'Edit Campaign' : 'Create New Campaign'}
        </Title>
        <Text style={{ color: themeToken.colorTextSecondary }}>Design your email campaign and choose your audience</Text>
      </div>

      <Form form={form} layout="vertical">

        {/* ── Campaign metadata ── */}
        <Card style={{ borderRadius: 14, border: `1px solid ${themeToken.colorBorderSecondary}`, boxShadow: '0 1px 4px rgba(0,0,0,0.04)', marginBottom: 16 }}>
          <Text strong style={{ fontSize: 14, color: themeToken.colorText, display: 'block', marginBottom: 16 }}>
            Campaign Details
          </Text>
          <Row gutter={[16, 0]}>
            <Col xs={24} md={12}>
              <Form.Item name="name" label="Campaign Name" rules={[{ required: true, message: 'Please enter a campaign name' }]}>
                <Input placeholder="e.g., May Newsletter" style={{ borderRadius: 10 }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="subject"
                label={
                  <span>
                    Email Subject{' '}
                    <span style={{ fontWeight: 400, fontSize: 12, color: subjectColor, marginLeft: 4 }}>
                      {subjectLen} / 80
                    </span>
                  </span>
                }
                rules={[{ required: true, message: 'Please enter an email subject line' }]}
              >
                <Input
                  prefix={<MailOutlined style={{ color: '#9ca3af' }} />}
                  placeholder="e.g., 🎉 Our biggest sale ever"
                  style={{ borderRadius: 10 }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="preview_text" label="Preview Text" extra="Snippet shown in inbox before opening">
                <Input
                  placeholder="Short teaser…"
                  maxLength={200}
                  suffix={
                    <Text style={{ fontSize: 11, color: previewTextValue.length > 90 ? '#f59e0b' : '#9ca3af' }}>
                      {previewTextValue.length}/90
                    </Text>
                  }
                  style={{ borderRadius: 10 }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="reply_to" label="Reply-To Email (optional)"
                rules={[{ type: 'email', message: 'Enter a valid email or leave blank' }]}
                style={{ marginBottom: 0 }}
              >
                <Input type="email" placeholder="replies@yourdomain.com" style={{ borderRadius: 10 }} />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* ── Audience ── */}
        <Card style={{ borderRadius: 14, border: `1px solid ${themeToken.colorBorderSecondary}`, boxShadow: '0 1px 4px rgba(0,0,0,0.04)', marginBottom: 16 }}>
          <Text strong style={{ fontSize: 14, color: themeToken.colorText, display: 'block', marginBottom: 4 }}>Audience</Text>
          <Text style={{ fontSize: 12, color: themeToken.colorTextSecondary, display: 'block', marginBottom: 20 }}>
            Choose who receives this campaign and optionally exclude specific contacts.
          </Text>
          <Row gutter={[32, 0]}>
            {/* ── Send To ── */}
            <Col xs={24} md={12}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                <div style={{ width: 28, height: 28, borderRadius: 8, background: '#EEF6D0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <UserAddOutlined style={{ color: '#9DC533', fontSize: 13 }} />
                </div>
                <Text strong style={{ fontSize: 13 }}>Send To</Text>
                <Text style={{ fontSize: 12, color: themeToken.colorTextTertiary }}>— who receives this email</Text>
              </div>
              <Form.Item
                name="list_id"
                label="Mailing Lists"
                rules={[{ required: true, type: 'array', min: 1, message: 'Select at least one list' }]}
              >
                <Select mode="multiple" placeholder="Select one or more lists" optionFilterProp="label" style={{ width: '100%' }} onChange={recalcReach}>
                  {lists.map(l => (
                    <Option key={l.id} value={l.id} label={l.name}>
                      {l.name}{l.subscriber_count !== undefined ? ` (${l.subscriber_count})` : ''}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              {uniqueCount !== null && (
                <div style={{ marginTop: -12, marginBottom: 16, padding: '6px 12px', background: '#EEF6D0', borderRadius: 6, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <UserAddOutlined style={{ color: '#9DC533', fontSize: 13 }} />
                  <Text style={{ fontSize: 13, color: '#5a7a1a' }}>
                    This campaign will reach <strong>{uniqueCount.toLocaleString()}</strong> active unique subscriber{uniqueCount !== 1 ? 's' : ''} (suppressed and excluded contacts removed)
                  </Text>
                </div>
              )}
              <Form.Item
                name="manual_emails"
                label={<span><UserAddOutlined style={{ marginRight: 6, color: '#6366f1' }} />Add Emails Manually</span>}
                extra="Type an email and press Enter"
                style={{ marginBottom: 0 }}
              >
                <Select
                  mode="tags"
                  placeholder="name@example.com, ..."
                  tokenSeparators={[',', ' ']}
                  style={{ width: '100%' }}
                  open={false}
                  suffixIcon={null}
                />
              </Form.Item>
            </Col>

            {/* ── Exclude ── */}
            <Col xs={24} md={12} style={{ borderLeft: `1px solid ${themeToken.colorBorderSecondary}`, paddingLeft: 32 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                <div style={{ width: 28, height: 28, borderRadius: 8, background: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <StopOutlined style={{ color: '#ef4444', fontSize: 13 }} />
                </div>
                <Text strong style={{ fontSize: 13 }}>Exclude</Text>
                <Text style={{ fontSize: 12, color: themeToken.colorTextTertiary }}>— contacts to skip</Text>
              </div>
              <Form.Item name="exclude_list_ids" label="Exclude Lists">
                <Select mode="multiple" placeholder="Select lists to suppress" optionFilterProp="label" style={{ width: '100%' }} onChange={recalcReach}>
                  {lists.map(l => (
                    <Option key={l.id} value={l.id} label={l.name}>
                      {l.name}{l.subscriber_count !== undefined ? ` (${l.subscriber_count})` : ''}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                name="exclude_emails"
                label={<span><StopOutlined style={{ marginRight: 6, color: '#ef4444' }} />Exclude Emails</span>}
                extra="Type an email and press Enter"
                style={{ marginBottom: 0 }}
              >
                <Select
                  mode="tags"
                  placeholder="skip@example.com, ..."
                  tokenSeparators={[',', ' ']}
                  style={{ width: '100%' }}
                  open={false}
                  suffixIcon={null}
                  onChange={recalcReach}
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* ── Email builder ── */}
        <Card
          style={{ borderRadius: 14, border: `1px solid ${themeToken.colorBorderSecondary}`, boxShadow: '0 1px 4px rgba(0,0,0,0.04)', marginBottom: 16 }}
          styles={{ body: { padding: 0 } }}
        >
          <div style={{
            padding: '14px 20px',
            borderBottom: '1px solid #f1f5f9',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderRadius: '14px 14px 0 0',
            background: '#fafafa',
          }}>
            <div>
              <Text strong style={{ fontSize: 14, color: '#374151' }}>Email Builder</Text>
              <Text style={{ fontSize: 12, color: '#9ca3af', marginLeft: 10 }}>
                Drag blocks from the right panel · Use the{' '}
                <span style={{
                  background: '#ede9fe', color: '#6366f1',
                  borderRadius: 4, padding: '1px 6px', fontSize: 11, fontWeight: 600,
                }}>
                  {'{M}'}
                </span>
                {' '}button inside any text block to insert merge tags
              </Text>
            </div>
            <Space size={8}>
              {!editorReady && (
                <Space size={6}>
                  <Spin size="small" />
                  <Text style={{ fontSize: 12, color: '#9ca3af' }}>Loading builder…</Text>
                </Space>
              )}
              <Tooltip title="Load a saved template into the builder">
                <Button
                  size="small" icon={<FolderOpenOutlined />}
                  onClick={openLoadTemplates}
                  style={{ borderRadius: 8, fontSize: 12 }}
                >
                  Load Template
                </Button>
              </Tooltip>
              <Tooltip title="Save current design as a reusable template">
                <Button
                  size="small" icon={<SaveOutlined />}
                  onClick={() => setSaveTemplModal(true)}
                  style={{ borderRadius: 8, fontSize: 12, background: '#ede9fe', borderColor: '#c4b5fd', color: '#6366f1' }}
                >
                  Save as Template
                </Button>
              </Tooltip>
            </Space>
          </div>

          {/* Wrapper clips Unlayer's branding footer (44px) via overflow:hidden */}
          <div style={{ overflow: 'hidden', height: 680, borderRadius: '0 0 14px 14px' }}>
            <EmailEditor
              ref={emailEditorRef}
              onReady={onEditorReady}
              style={{ height: 724 }}
              options={{
                displayMode: 'email',
                mergeTags: MERGE_TAGS,
                features: {
                  stockImages: { enabled: true },
                  userUploads:  true,
                },
                tools: {
                  video: { enabled: false },
                },
                appearance: {
                  theme: 'light',
                  panels: { tools: { dock: 'right' } },
                },
                fonts: {
                  showDefaultFonts: true,
                },
              }}
            />
          </div>
        </Card>

        {/* ── Inline test-send panel ── */}
        {testModal && (
          <Card style={{ borderRadius: 12, border: '1px solid #e0e7ff', background: '#f5f3ff', marginBottom: 16 }}>
            <Text strong style={{ display: 'block', marginBottom: 10 }}>Send test email to:</Text>
            <Space.Compact style={{ width: '100%' }}>
              <Input
                value={testEmail}
                onChange={e => setTestEmail(e.target.value)}
                placeholder="test@example.com"
                type="email"
                style={{ borderRadius: '10px 0 0 10px' }}
                onPressEnter={handleTestSend}
              />
              <Button type="primary" loading={testSending} onClick={handleTestSend} style={{ borderRadius: '0 10px 10px 0' }}>Send</Button>
              <Button onClick={() => { setTestModal(false); setTestEmail(''); }} style={{ marginLeft: 8, borderRadius: 10 }}>Cancel</Button>
            </Space.Compact>
          </Card>
        )}

        {/* ── Action bar ── */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 4, flexWrap: 'wrap' }}>
          <Button size="large" onClick={() => navigate('/campaigns')} style={{ borderRadius: 10 }}>Cancel</Button>
          <Button size="large" icon={<SendOutlined />} onClick={() => setTestModal(!testModal)} style={{ borderRadius: 10 }}>Send Test</Button>
          <Button
            loading={saving} size="large" onClick={handleSubmit}
            style={{ borderRadius: 10, fontWeight: 600, minWidth: 160, borderColor: themeToken.colorPrimary, color: themeToken.colorPrimary }}
          >
            {isEdit ? 'Save Draft' : 'Save as Draft'}
          </Button>
          {isDraft && (
            <>
              <Button
                icon={<SendOutlined />} size="large"
                onClick={() => { setSendMode('all'); setSendDaily(200); setSendDayMode('every'); setSendDaysSelected([1, 2, 3, 4, 5]); setSendFixedTime(false); setSendDailyClock(null); setSendDayTimes({}); setSendTiming('now'); setScheduleDate(null); setScheduleModal(true); }}
                style={{ borderRadius: 10, background: '#9DC533', borderColor: '#9DC533', color: '#fff', fontWeight: 600, minWidth: 130 }}
              >
                Send…
              </Button>
            </>
          )}
        </div>
      </Form>

      {/* ── Save as Template modal ── */}
      <Modal
        title={<Space><SaveOutlined style={{ color: '#6366f1' }} /><span>Save as Template</span></Space>}
        open={saveTemplModal}
        onCancel={() => { setSaveTemplModal(false); setTemplName(''); }}
        onOk={handleSaveTemplate}
        okText="Save Template"
        okButtonProps={{ loading: templSaving, style: { borderRadius: 8 } }}
        cancelButtonProps={{ style: { borderRadius: 8 } }}
        destroyOnHidden
        width={420}
      >
        <div style={{ marginTop: 16 }}>
          <Text strong style={{ display: 'block', marginBottom: 8 }}>Template Name</Text>
          <Input
            placeholder="e.g., Monthly Newsletter Layout"
            value={templName}
            onChange={e => setTemplName(e.target.value)}
            onPressEnter={handleSaveTemplate}
            maxLength={80}
            style={{ borderRadius: 10 }}
            autoFocus
          />
          <Text style={{ fontSize: 12, color: themeToken.colorTextTertiary, marginTop: 6, display: 'block' }}>
            The current builder design will be saved and can be reloaded into any future campaign.
          </Text>
        </div>
      </Modal>

      {/* ── Load Template modal ── */}
      <Modal
        title={<Space><FolderOpenOutlined style={{ color: '#6366f1' }} /><span>Load Template</span></Space>}
        open={loadTemplModal}
        onCancel={() => setLoadTemplModal(false)}
        footer={null}
        destroyOnHidden
        width={560}
      >
        {templLoading ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}><Spin /></div>
        ) : templates.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No saved templates yet. Design an email and click 'Save as Template'."
            style={{ padding: '40px 0' }}
          />
        ) : (
          <List
            dataSource={templates}
            style={{ marginTop: 8 }}
            renderItem={tpl => (
              <List.Item
                style={{
                  cursor: 'pointer', borderRadius: 10, padding: '12px 14px', marginBottom: 8,
                  border: `1px solid ${themeToken.colorBorderSecondary}`, background: themeToken.colorBgLayout,
                  transition: 'background 0.15s',
                }}
                onMouseEnter={e => e.currentTarget.style.background = '#f0f4ff'}
                onMouseLeave={e => e.currentTarget.style.background = themeToken.colorBgLayout}
                onClick={() => handleLoadTemplate(tpl)}
                actions={[
                  <Tooltip title="Delete template" key="del">
                    <Button
                      type="text" danger size="small"
                      icon={<DeleteOutlined />}
                      onClick={e => handleDeleteTemplate(tpl.id, e)}
                    />
                  </Tooltip>,
                ]}
              >
                <List.Item.Meta
                  avatar={
                    <div style={{
                      width: 38, height: 38, borderRadius: 10,
                      background: '#ede9fe', display: 'flex',
                      alignItems: 'center', justifyContent: 'center',
                    }}>
                      <MailOutlined style={{ color: '#6366f1', fontSize: 17 }} />
                    </div>
                  }
                  title={<Text strong style={{ fontSize: 13 }}>{tpl.name}</Text>}
                  description={
                    <Text style={{ fontSize: 12, color: themeToken.colorTextTertiary }}>
                      Saved {new Date(tpl.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </Text>
                  }
                />
              </List.Item>
            )}
          />
        )}
      </Modal>

      {/* ── Send modal (all-at-once or daily batches, now or scheduled) ── */}
      <Modal
        title={<Space><SendOutlined style={{ color: '#9DC533' }} /><span>Send Campaign</span></Space>}
        open={scheduleModal}
        onCancel={() => { setScheduleModal(false); setScheduleDate(null); }}
        onOk={handleUnifiedSend}
        okText={sendTiming === 'schedule' ? (sendMode === 'daily' ? 'Schedule Drip' : 'Schedule') : (sendMode === 'daily' ? 'Start Drip Send' : 'Send Now')}
        okButtonProps={{ loading: scheduling, style: { borderRadius: 8 } }}
        cancelButtonProps={{ style: { borderRadius: 8 } }}
        destroyOnHidden
        width={480}
      >
        <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>How to send</Text>
            <Radio.Group value={sendMode} onChange={e => setSendMode(e.target.value)} style={{ width: '100%' }}>
              <Radio.Button value="all" style={{ width: '50%', textAlign: 'center' }}>All at once</Radio.Button>
              <Radio.Button value="daily" style={{ width: '50%', textAlign: 'center' }}>Daily batches</Radio.Button>
            </Radio.Group>
          </div>

          {sendMode === 'daily' && (
            <div>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>Emails per day</Text>
              <InputNumber min={1} max={100000} step={50} value={sendDaily} onChange={v => setSendDaily(v || 0)} style={{ width: '100%', borderRadius: 8 }} size="large" />
            </div>
          )}

          {sendMode === 'daily' && (
            <div>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>Send on</Text>
              <Radio.Group value={sendDayMode} onChange={e => setSendDayMode(e.target.value)} style={{ width: '100%', marginBottom: sendDayMode === 'specific' ? 10 : 0 }}>
                <Radio.Button value="every" style={{ width: '50%', textAlign: 'center' }}>All days</Radio.Button>
                <Radio.Button value="specific" style={{ width: '50%', textAlign: 'center' }}>Specific days</Radio.Button>
              </Radio.Group>
              {sendDayMode === 'specific' && (
                <Space size={6} wrap>
                  {WEEKDAYS.map(d => {
                    const active = sendDaysSelected.includes(d.value);
                    return (
                      <Button
                        key={d.value}
                        size="small"
                        title={d.full}
                        onClick={() => toggleSendDay(d.value)}
                        style={{
                          borderRadius: 8, minWidth: 38, fontWeight: 600,
                          background: active ? '#9DC533' : undefined,
                          borderColor: active ? '#9DC533' : undefined,
                          color: active ? '#fff' : undefined,
                        }}
                      >
                        {d.label}
                      </Button>
                    );
                  })}
                </Space>
              )}
            </div>
          )}

          {sendMode === 'daily' && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: sendFixedTime ? 10 : 0 }}>
                <Text strong>Fixed time each day</Text>
                <Switch checked={sendFixedTime} onChange={setSendFixedTime} />
              </div>
              {sendFixedTime ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <Select showSearch style={{ width: '100%' }} value={sendDailyTz} onChange={setSendDailyTz} optionFilterProp="label" options={TIMEZONES} />
                  {sendDayMode === 'specific' ? (
                    sendDaysSelected.length ? (
                      [...sendDaysSelected]
                        .sort((a, b) => (a === 0 ? 7 : a) - (b === 0 ? 7 : b))
                        .map(d => (
                          <div key={d} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Text style={{ width: 42, fontSize: 13, fontWeight: 500 }}>{dayLabel(d)}</Text>
                            <TimePicker
                              use12Hours format="hh:mm A" style={{ flex: 1 }}
                              value={sendDayTimes[d] || null}
                              onChange={val => setSendDayTimes(pt => ({ ...pt, [d]: val }))}
                              placeholder="Time"
                            />
                          </div>
                        ))
                    ) : (
                      <Text style={{ fontSize: 12, color: themeToken.colorTextTertiary }}>
                        Select at least one day above to set its time.
                      </Text>
                    )
                  ) : (
                    <TimePicker
                      use12Hours format="hh:mm A" style={{ width: '100%' }}
                      value={sendDailyClock}
                      onChange={setSendDailyClock}
                      placeholder="Time"
                    />
                  )}
                </div>
              ) : (
                <Text style={{ fontSize: 12, color: themeToken.colorTextTertiary }}>
                  Off — each batch fires ~24h after the previous one, whenever that happened to be.
                </Text>
              )}
            </div>
          )}

          <div>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>When</Text>
            <Radio.Group value={sendTiming} onChange={e => setSendTiming(e.target.value)} style={{ width: '100%' }}>
              <Radio.Button value="now" style={{ width: '50%', textAlign: 'center' }}>Start now</Radio.Button>
              <Radio.Button value="schedule" style={{ width: '50%', textAlign: 'center' }}>Schedule start</Radio.Button>
            </Radio.Group>
          </div>

          {sendTiming === 'schedule' && (
            <>
              <div>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>Timezone</Text>
                <Select showSearch style={{ width: '100%' }} size="large" value={scheduleTz} onChange={setScheduleTz} optionFilterProp="label" options={TIMEZONES} />
              </div>
              <div>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>{sendMode === 'daily' ? 'First batch date & time' : 'Date & time'}</Text>
                <DatePicker
                  showTime={{ use12Hours: true, format: 'hh:mm A' }}
                  format="YYYY-MM-DD hh:mm A"
                  style={{ width: '100%', borderRadius: 10 }}
                  size="large"
                  disabledDate={d => d && d < dayjs().startOf('day')}
                  onChange={val => setScheduleDate(val ? val.toDate() : null)}
                />
              </div>
            </>
          )}

          <Text style={{ color: themeToken.colorTextTertiary, fontSize: 13 }}>
            {sendMode === 'daily'
              ? <>
                  Sends up to <strong>{sendDaily || 0}</strong>/day{sendTiming === 'schedule' ? ' starting at the chosen time' : ''}
                  {sendDayMode === 'specific' && sendDaysSelected.length > 0
                    ? <> on <strong>{[...sendDaysSelected].sort((a, b) => (a === 0 ? 7 : a) - (b === 0 ? 7 : b)).map(dayLabel).join(', ')}</strong> only</>
                    : ''}
                  {sendFixedTime && sendDayMode === 'specific' && Object.keys(sendDayTimes).length > 0
                    ? <> at <strong>
                        {[...sendDaysSelected]
                          .filter(d => sendDayTimes[d])
                          .sort((a, b) => (a === 0 ? 7 : a) - (b === 0 ? 7 : b))
                          .map(d => `${dayLabel(d)} ${dayjs(sendDayTimes[d]).format('hh:mm A')}`)
                          .join(' · ')} {sendDailyTz}
                      </strong></>
                    : (sendFixedTime && sendDayMode !== 'specific' && sendDailyClock ? <> at <strong>{dayjs(sendDailyClock).format('hh:mm A')} {sendDailyTz}</strong> each day</> : '')}
                  , continuing until the whole list is done. No subscriber is emailed twice. You can change this schedule anytime while it's running.
                </>
              : <>Sends to the entire selected list {sendTiming === 'schedule' ? 'at the scheduled time' : 'immediately'}.</>}
          </Text>
        </div>
      </Modal>
    </div>
  );
};

export default CreateCampaign;
